import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Wordmark } from "./Brand";
import { LanguageToggle } from "./LanguageToggle";
import { useT } from "@/context/LanguageContext";
import { KOFI_URL } from "@/lib/links";
import type { TranslationKey } from "@/lib/i18n";

/**
 * `external` forces a real navigation instead of a client-side one. /news is a
 * static page written to S3, so a react-router <Link> would render the SPA's
 * archive route and the pre-rendered Wire would never be seen by anyone
 * browsing the site.
 */
const LINKS: Array<{ to: string; label: TranslationKey; end?: boolean; external?: boolean }> = [
  { to: "/", label: "nav.home", end: true },
  { to: "/fixtures", label: "nav.fixtures" },
  { to: "/standings", label: "nav.standings" },
  { to: "/players", label: "nav.players" },
  { to: "/nations", label: "nav.nations" },
  { to: "/news", label: "nav.news", external: true },
];

export function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const t = useT();

  // Close the drawer on navigation, otherwise it hangs over the new page.
  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock the page behind the open drawer.
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ease-out
                  ${scrolled || open ? "border-b-2 border-ink-line bg-ink/[0.92] backdrop-blur-md" : "border-b-2 border-transparent"}`}
    >
      <div className="u-frame flex h-[var(--nav-h)] items-center justify-between gap-4 sm:gap-6">
        <Wordmark />

        <nav aria-label={t("nav.primary")} className="hidden md:block">
          <ul className="flex items-center gap-6 lg:gap-8">
            {LINKS.map((link) => {
              const desktopClass = (isActive: boolean) =>
                `u-display relative text-xs transition-colors duration-300
                 ${isActive ? "text-ink-bright" : "text-ink-muted hover:text-ink-bright"}
                 after:absolute after:-bottom-2 after:left-0 after:h-px after:bg-ember
                 after:transition-all after:duration-300 after:ease-out
                 ${isActive ? "after:w-full" : "after:w-0 hover:after:w-full"}`;

              return (
                <li key={link.to}>
                  {link.external ? (
                    <a href={link.to} className={desktopClass(false)}>
                      {t(link.label)}
                    </a>
                  ) : (
                    <NavLink
                      to={link.to}
                      end={link.end}
                      className={({ isActive }) => desktopClass(isActive)}
                    >
                      {t(link.label)}
                    </NavLink>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <LanguageToggle />

          <a
            href={KOFI_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="u-btn-donate hidden sm:inline-flex"
          >
            <HeartIcon />
            {t("nav.donate")}
          </a>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="mobile-menu"
            aria-label={open ? t("nav.closeMenu") : t("nav.openMenu")}
            className="grid h-10 w-10 shrink-0 place-items-center md:hidden"
          >
            <span className="relative block h-3.5 w-6">
              <span
                className={`absolute left-0 block h-0.5 w-full bg-ink-bright transition-all duration-300 ease-out
                            ${open ? "top-1.5 rotate-45" : "top-0"}`}
              />
              <span
                className={`absolute left-0 top-1.5 block h-0.5 w-full bg-ink-bright transition-opacity duration-200
                            ${open ? "opacity-0" : "opacity-100"}`}
              />
              <span
                className={`absolute left-0 block h-0.5 w-full bg-ink-bright transition-all duration-300 ease-out
                            ${open ? "top-1.5 -rotate-45" : "top-3"}`}
              />
            </span>
          </button>
        </div>
      </div>

      {/* Mobile drawer: full-height panel, links at display size. */}
      <div
        id="mobile-menu"
        className={`u-frame flex h-[calc(100dvh-var(--nav-h))] flex-col justify-between
              overflow-y-auto border-t border-ink-line bg-ink pb-10 pt-8 md:hidden
              ${!open ? "hidden" : ""}`}
      >
        <ul className="flex flex-col gap-1">
          {LINKS.map((link) => {
            const drawerClass = (isActive: boolean) =>
              `u-display block border-b border-ink-line py-4 text-2xl
               ${isActive ? "text-ember" : "text-ink-bright"}`;

            return (
              <li key={link.to}>
                {link.external ? (
                  <a href={link.to} className={drawerClass(false)}>
                    {t(link.label)}
                  </a>
                ) : (
                  <NavLink
                    to={link.to}
                    end={link.end}
                    className={({ isActive }) => drawerClass(isActive)}
                  >
                    {t(link.label)}
                  </NavLink>
                )}
              </li>
            );
          })}
        </ul>

        <a
          href={KOFI_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="u-btn-donate justify-center py-3.5"
        >
          <HeartIcon />
          {t("nav.support")}
        </a>
      </div>
    </header>
  );
}

function HeartIcon() {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="currentColor" aria-hidden="true">
      <path d="M8 14.5S1 10.2 1 5.6A3.6 3.6 0 0 1 8 4.2a3.6 3.6 0 0 1 7 1.4c0 4.6-7 8.9-7 8.9Z" />
    </svg>
  );
}
